import { Card, Skeleton } from "@attmos/flowstack-ui";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useProject, useProjectRuns, useRememberProject } from "@/api/projects";
import { useRunFeed } from "@/api/runs";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { SubmitChoices } from "@/components/projects/SubmitChoices";
import { groupRunFeed } from "@/components/runs/grouped/group-runs";
import { analysisRows } from "@/routes/analysis/analysis-flow-runs";

/** Lazy, as on the Runs tab, so the grouped table stays out of the entry
 * chunk `fonts.spec.ts` budgets (flowstack#1799). */
const FlowRunGroupedTable = React.lazy(() =>
  import("@/components/runs/grouped/FlowRunGroupedTable").then((m) => ({
    default: m.FlowRunGroupedTable,
  }))
);

interface RouteParams {
  slug?: string;
  [key: string]: string | undefined;
}

/**
 * Project home (fs#1894): the same two entry points for new work on every
 * project, Submit a Tile and Submit a Flow, then the project's counts, then
 * its history grouped by FLOW RUN.
 *
 * Nothing here names a pipeline: which tile or flow to run is picked on the
 * next page. The history is the Runs tab's table over this project's rows,
 * gathered the way the Analysis pages gather them (`analysisRows`: the tenant
 * feed's rows for this slug, whose flow-run rollups describe the same
 * snapshot, plus any project-feed row the tenant feed did not return).
 *
 * It has no "By" column, like the Runs tab and the Dashboard: runs record no
 * submitter, and `submitted_by` is the id of the session that asked (#960).
 */
export function ProjectDetailPage(): JSX.Element | null {
  const { slug = "" } = useParams<RouteParams>();
  const project = useProject(slug);
  useRememberProject(slug);
  const runs = useProjectRuns(slug);
  const feed = useRunFeed();

  const entries = React.useMemo(
    () =>
      groupRunFeed(
        analysisRows(feed.data?.runs ?? [], runs.data ?? [], slug),
        feed.data?.flowRuns ?? []
      ),
    [feed.data, runs.data, slug]
  );

  if (project.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-1/4" />
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-5 w-1/3" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (project.isError) {
    const isNotFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <EmptyState
        title={isNotFound ? "Project not found." : "Could not load project."}
        description={
          isNotFound
            ? `No project with slug ${slug ? `'${slug}'` : "(empty)"}.`
            : project.error instanceof Error
              ? project.error.message
              : "The FlowStack server returned an error."
        }
      />
    );
  }

  if (!project.data) return null;

  const breadcrumbs = (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Link to="/projects" className="hover:text-foreground">
        Projects
      </Link>
      <span aria-hidden>·</span>
      <span className="font-mono">{project.data.slug}</span>
    </div>
  );

  // Either feed alone is enough to list the history; only both failing is an
  // error, and it names the tenant feed's, which carries the rollups.
  const historyLoading = runs.isLoading || feed.isLoading;
  const historyError = runs.isError && feed.isError ? (feed.error ?? runs.error) : null;

  return (
    <div className="flex flex-col gap-6">
      {breadcrumbs}
      <PageHeader
        title={project.data.display_name}
        description={project.data.description ?? undefined}
      />

      <SubmitChoices slug={project.data.slug} />

      <div className="grid grid-cols-3 gap-4 sm:max-w-md">
        <StatTile label="Runs" value={project.data.run_count} />
        <StatTile label="Active" value={project.data.active_run_count} />
        <StatTile label="Artifacts" value={project.data.artifact_count} />
      </div>

      <section aria-labelledby="runs-heading" className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <h2 id="runs-heading" className="font-display text-xl font-semibold tracking-tight">
            Runs
          </h2>
          {/* fs#1797: this is the project's most recent runs; the full,
              searchable feed is the Runs page. */}
          <Link to="/runs" className="text-xs text-muted-foreground hover:text-foreground">
            All runs
          </Link>
        </div>
        {historyLoading ? (
          <Skeleton className="h-[400px] w-full" />
        ) : historyError ? (
          <EmptyState
            title="Could not load runs."
            description={historyError instanceof Error ? historyError.message : "Unknown error."}
          />
        ) : entries.length === 0 ? (
          <EmptyState
            title="No runs yet."
            description="Submit a Tile or a Flow above; its runs are listed here."
          />
        ) : (
          <React.Suspense fallback={<Skeleton className="h-[400px] w-full" />}>
            <FlowRunGroupedTable
              entries={entries}
              showProjectColumn={false}
              renderRunLink={(runId, children) => (
                <Link
                  to={`/runs/${encodeURIComponent(runId)}`}
                  className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {children}
                </Link>
              )}
            />
          </React.Suspense>
        )}
      </section>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <Card className="p-card">
      <p className="label-micro text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-semibold tabular-nums">{value}</p>
    </Card>
  );
}
